import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import CarCard from "@/components/CarCard";
import AvailabilitySearch from "@/components/AvailabilitySearch";
import { Seo } from "@/components/Seo";
import {
  CITIES, SERVICE_META, SERVICE_ORDER, hubSlug, childUrl, childLabel,
  cityImage, cityServiceLd,
} from "@/data/cityLocations";
import {
  ChevronDown, ChevronRight, Phone, MapPin, Train, Car, ArrowRight, CheckCircle2,
} from "lucide-react";

const SITE = "https://belorentals.com";

export default function CityHub({ citySlug }) {
  const cfg = CITIES[citySlug];
  const [cars, setCars] = useState([]);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    api.get("/cars").then((r) => setCars(r.data || [])).catch(() => {});
  }, []);

  if (!cfg) return null;

  const path = `/${hubSlug(citySlug)}`;
  const h1 = `Car Rental in ${cfg.cityName}, MA`;
  const breadcrumb = [
    { name: "Home", to: "/" },
    { name: h1, to: path },
  ];
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumb.map((b, i) => ({
      "@type": "ListItem", position: i + 1, name: b.name, item: `${SITE}${b.to}`,
    })),
  };
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: cfg.faqs.map((f) => ({
      "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const serviceLd = cityServiceLd(citySlug, h1);
  const heroImg = cityImage(citySlug);

  const pref = ["Economy", "Sedan", "SUV", "Minivan"];
  const rankCar = (c) => { const i = pref.indexOf(c.category); return i === -1 ? 99 : i; };
  const featured = [...cars].sort((a, b) => rankCar(a) - rankCar(b)).slice(0, 3);

  return (
    <div data-testid={`city-hub-${citySlug}`} className="bg-white">
      <Seo
        title={cfg.metaTitle}
        description={cfg.metaDescription}
        path={path}
        keywords={cfg.keywords}
        image={heroImg}
        jsonLd={[breadcrumbLd, faqLd, serviceLd]}
      />

      {/* Hero + breadcrumb */}
      <section className="relative pt-28 pb-14 px-5 sm:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-4xl mx-auto">
          <nav aria-label="Breadcrumb" data-testid="breadcrumb">
            <ol className="flex flex-wrap items-center gap-1.5 text-sm text-slate-500">
              {breadcrumb.map((b, i) => (
                <li key={b.to} className="flex items-center gap-1.5">
                  {i < breadcrumb.length - 1 ? (
                    <Link to={b.to} className="hover:text-blue-600 transition-colors">{b.name}</Link>
                  ) : (
                    <span className="text-slate-800 font-medium" aria-current="page">{b.name}</span>
                  )}
                  {i < breadcrumb.length - 1 && <ChevronRight size={14} className="text-slate-300" />}
                </li>
              ))}
            </ol>
          </nav>

          <div className="mt-6 text-center">
            <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-600/10 text-blue-700 text-sm font-medium mb-5">
              <MapPin size={15} /> {cfg.heroBadge}
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900">{h1}</h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">{cfg.heroCopy}</p>
            <div className="mt-9 max-w-2xl mx-auto">
              <AvailabilitySearch />
              <p className="mt-4 text-sm text-slate-500 flex items-center justify-center gap-1.5">
                <Phone size={14} className="text-slate-400" /> Prefer to talk? Call{" "}
                <a href="tel:+19782211451" className="text-blue-600 font-semibold hover:underline">(978) 221-1451</a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Hero image */}
      <div className="max-w-6xl mx-auto px-5 sm:px-8 -mt-2">
        <img
          src={heroImg}
          alt={`Car rental in ${cfg.cityName}, MA — Belo Rentals`}
          loading="eager"
          className="w-full aspect-[21/9] object-cover rounded-3xl shadow-card"
          data-testid="city-hero-image"
        />
      </div>

      {/* Local intro */}
      <section className="py-14 px-5 sm:px-8 max-w-3xl mx-auto">
        <h2 className="font-heading text-3xl font-bold text-slate-900">Renting a car in {cfg.cityName}</h2>
        <p className="mt-5 text-slate-600 leading-relaxed">{cfg.localIntro}</p>
      </section>

      {/* Service clusters */}
      <section className="pb-4 px-5 sm:px-8 max-w-6xl mx-auto">
        <h2 className="font-heading text-3xl font-bold text-slate-900 text-center">Rental options in {cfg.cityName}</h2>
        <p className="text-slate-500 mt-3 text-center max-w-2xl mx-auto">Pick the length that fits your trip — daily economy, a full week, or a long-term monthly rental.</p>
        <div className="grid sm:grid-cols-3 gap-5 mt-10" data-testid="service-cards">
          {SERVICE_ORDER.map((s) => (
            <Link
              key={s}
              to={childUrl(citySlug, s)}
              data-testid={`service-card-${s}`}
              className="group bg-slate-50 rounded-2xl p-6 border border-slate-100 hover:border-blue-300 hover:shadow-card transition-all"
            >
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-blue-600 text-white mb-4"><Car size={20} /></span>
              <h3 className="font-heading font-bold text-slate-900 group-hover:text-blue-700 transition-colors">{childLabel(citySlug, s)}</h3>
              <p className="text-slate-600 text-sm mt-1.5 leading-relaxed">{SERVICE_META[s].tagline}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-blue-600 text-sm font-semibold">Learn more <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" /></span>
            </Link>
          ))}
        </div>
      </section>

      {/* Transportation context + local use cases */}
      <section className="py-16 px-5 sm:px-8 max-w-6xl mx-auto grid lg:grid-cols-2 gap-10">
        <div>
          <div className="flex items-center gap-2.5 mb-4">
            <span className="grid place-items-center w-9 h-9 rounded-lg bg-slate-800 text-white"><Train size={18} /></span>
            <h2 className="font-heading text-2xl font-bold text-slate-900">Getting around {cfg.cityName}</h2>
          </div>
          <ul className="space-y-3" data-testid="transportation-context">
            {cfg.transportationContext.map((tc, i) => (
              <li key={i} className="flex items-start gap-3 text-slate-600 text-sm leading-relaxed">
                <MapPin size={17} className="text-blue-500 shrink-0 mt-0.5" /> <span>{tc}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="flex items-center gap-2.5 mb-4">
            <span className="grid place-items-center w-9 h-9 rounded-lg bg-blue-600 text-white"><CheckCircle2 size={18} /></span>
            <h2 className="font-heading text-2xl font-bold text-slate-900">When people rent here</h2>
          </div>
          <ul className="space-y-3" data-testid="local-use-cases">
            {cfg.localUseCases.map((u, i) => (
              <li key={i} className="flex items-start gap-3 text-slate-600 text-sm leading-relaxed">
                <CheckCircle2 size={17} className="text-emerald-500 shrink-0 mt-0.5" /> <span>{u}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Fleet preview */}
      {featured.length > 0 && (
        <section className="pb-16 px-5 sm:px-8 max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-8 gap-4">
            <h2 className="font-heading text-3xl font-bold text-slate-900">Popular cars for {cfg.cityName}</h2>
            <Link to="/fleet" className="text-blue-600 font-semibold text-sm hover:underline shrink-0">View all →</Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((car, i) => <CarCard key={car.id} car={car} index={i} />)}
          </div>
        </section>
      )}

      {/* Nearby cities + hub links */}
      <section className="pb-16 px-5 sm:px-8 max-w-6xl mx-auto">
        <h2 className="font-heading text-2xl font-bold text-slate-900 text-center">Explore more Belo Rentals</h2>
        <div className="mt-8 flex flex-wrap justify-center gap-3" data-testid="nearby-cities">
          {(cfg.nearbyCities || []).filter((n) => CITIES[n]).map((n) => (
            <Link key={n} to={`/${hubSlug(n)}`} className="px-4 py-2 rounded-full bg-slate-50 border border-slate-200 text-slate-700 text-sm font-medium hover:border-blue-400 hover:text-blue-700 transition-colors">
              Car Rental in {CITIES[n].cityName}
            </Link>
          ))}
          <Link to="/logan-airport-car-rental" className="px-4 py-2 rounded-full bg-slate-50 border border-slate-200 text-slate-700 text-sm font-medium hover:border-blue-400 hover:text-blue-700 transition-colors">
            Logan Airport Car Rental
          </Link>
          {citySlug !== "boston" && (
            <Link to="/car-rental-boston-ma" className="px-4 py-2 rounded-full bg-slate-50 border border-slate-200 text-slate-700 text-sm font-medium hover:border-blue-400 hover:text-blue-700 transition-colors">
              Car Rental in Boston
            </Link>
          )}
          <Link to="/car-rental-locations" className="px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors">
            All locations we serve
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-5 sm:px-8 bg-slate-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-3xl font-bold text-slate-900 text-center">Frequently asked questions</h2>
          <div className="mt-10 space-y-3" data-testid="faq-list">
            {cfg.faqs.map((f, i) => (
              <div key={i} data-testid={`faq-item-${i}`} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  aria-expanded={openFaq === i}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                >
                  <span className="font-medium text-slate-900">{f.q}</span>
                  <ChevronDown size={18} className={`shrink-0 text-slate-400 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <div className="px-5 pb-5 text-slate-600 text-sm leading-relaxed">{f.a}</div>}
              </div>
            ))}
          </div>
          <div className="sr-only">
            {cfg.faqs.map((f, i) => (<div key={i}><h3>{f.q}</h3><p>{f.a}</p></div>))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-5 sm:px-8">
        <div className="max-w-4xl mx-auto rounded-3xl bg-gradient-to-r from-slate-900 to-blue-900 text-white text-center px-8 py-14">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold">Ready to book in {cfg.cityName}?</h2>
          <p className="mt-4 text-blue-100 max-w-xl mx-auto">Reserve a clean, well-maintained car in minutes. From $50/day.</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/fleet" className="px-7 py-3.5 rounded-full bg-white text-slate-900 font-semibold hover:bg-blue-50 transition-colors">Book your car</Link>
            <Link to="/contact" className="px-7 py-3.5 rounded-full border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors">Contact us</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
